import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AdvertisingHomePage, ContattiPage, GommistaPage, MeccatronicaPage, SoccorsoH24Page } from "@/components/viscar/ServicePages";
import { CarroattrezziMessinaPage, CarroattrezziTremestieriPage, CarroattrezziVillafrancaPage, SoccorsoAutostradalePage, SoccorsoMezziPesantiPage, SoccorsoProvinciaMessinaPage, SoccorsoStradaleMessinaPage, SoccorsoStradaleMilazzoPage, SoccorsoStradaleTaorminaPage } from "@/components/viscar/LocalSeoPages";
import PhotoManagementPage from "@/components/viscar/PhotoManagementPage";
import { AuthProvider } from "@/components/viscar/auth";
import { PrivateDashboardPage, PrivateLoginPage } from "@/components/viscar/PrivateAreaPages";
import { ProtectedRoute } from "@/components/viscar/ProtectedRoute";
import { CookieConsentBanner, CookiePolicyPage, PrivacyPolicyPage } from "@/components/viscar/CookieConsent";
import { recordAnalyticsEvent } from "@/components/viscar/analytics";
import { SeoManager } from "@/components/viscar/SeoManager";
import { TextEditorControls, TextEditorProvider } from "@/components/viscar/TextEditor";
import { Toaster } from "@/components/ui/sonner";
import { buildFleet, DEFAULT_IMAGE_SLOTS, DEFAULT_INTERVENTION_PHOTOS, FLEET_SLOT_KEYS } from "@/components/viscar/config";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const defaultPhotoConfig = {
  slots: DEFAULT_IMAGE_SLOTS,
  galleryPhotos: DEFAULT_INTERVENTION_PHOTOS,
  library: [],
  updated_at: "",
};

const RouteScrollManager = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const normalizedPath = location.pathname.length > 1 && location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;

    const params = new URLSearchParams(location.search);
    const hasLegacyFeedParams = params.has("feed") || params.has("p");

    if (normalizedPath !== location.pathname || hasLegacyFeedParams) {
      navigate(normalizedPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const targetId = location.hash.replace("#", "");
    const scrollTarget = () => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const timeoutId = window.setTimeout(scrollTarget, 80);
    return () => window.clearTimeout(timeoutId);
  }, [location]);

  useEffect(() => {
    recordAnalyticsEvent({
      eventType: "page_view",
      pagePath: location.pathname,
      pageTitle: document.title,
      sourceLabel: location.pathname,
    });
  }, [location.pathname]);

  return null;
};

const GlobalInteractionTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const handleClick = (event) => {
      const anchor = event.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        recordAnalyticsEvent({
          eventType: "phone_click",
          pagePath: window.location.pathname,
          pageTitle: document.title,
          target: href,
          sourceLabel: anchor.textContent?.trim() || "Telefono",
        });
      }
      if (href.includes("wa.me") || href.includes("whatsapp")) {
        recordAnalyticsEvent({
          eventType: "whatsapp_click",
          pagePath: window.location.pathname,
          pageTitle: document.title,
          target: href,
          sourceLabel: anchor.textContent?.trim() || "WhatsApp",
        });
      }
    };

    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, []);

  return <SeoManager pathname={location.pathname} />;
};

function App() {
  const [photoConfig, setPhotoConfig] = useState(defaultPhotoConfig);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  const imageSlots = useMemo(() => ({ ...DEFAULT_IMAGE_SLOTS, ...(photoConfig?.slots || {}) }), [photoConfig?.slots]);
  const usedSectionImages = useMemo(() => new Set(Object.values(imageSlots).filter(Boolean)), [imageSlots]);
  const nonFleetSectionImages = useMemo(
    () => new Set(Object.entries(imageSlots).filter(([key, value]) => value && !FLEET_SLOT_KEYS.includes(key)).map(([, value]) => value)),
    [imageSlots],
  );
  const galleryPhotos = useMemo(
    () => (photoConfig?.galleryPhotos || DEFAULT_INTERVENTION_PHOTOS).filter((photo) => !usedSectionImages.has(photo.image)),
    [photoConfig?.galleryPhotos, usedSectionImages],
  );
  const fleet = useMemo(() => buildFleet(imageSlots, nonFleetSectionImages), [imageSlots, nonFleetSectionImages]);

  useEffect(() => {
    const loadPhotoConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/viscar/photo-config`);
        if (!response.ok) {
          throw new Error("Load failed");
        }

        const data = await response.json();
        setPhotoConfig({
          slots: { ...DEFAULT_IMAGE_SLOTS, ...data.slots },
          galleryPhotos: data.gallery_photos,
          library: data.library,
          updated_at: data.updated_at,
        });
      } catch {
        setPhotoConfig(defaultPhotoConfig);
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadPhotoConfig();
  }, []);

  return (
    <AuthProvider>
      <TextEditorProvider>
        <BrowserRouter>
          <RouteScrollManager />
          <GlobalInteractionTracker />
          <Routes>
            <Route path="/" element={<SoccorsoH24Page imageSlots={imageSlots} fleet={fleet} galleryPhotos={galleryPhotos} />} />
            <Route path="/viscar-soccorso-stradale-h24" element={<AdvertisingHomePage imageSlots={imageSlots} />} />
            <Route path="/soccorso-h24" element={<SoccorsoH24Page imageSlots={imageSlots} fleet={fleet} galleryPhotos={galleryPhotos} />} />
            <Route path="/meccatronica" element={<MeccatronicaPage imageSlots={imageSlots} />} />
            <Route path="/gommista" element={<GommistaPage imageSlots={imageSlots} />} />
            <Route path="/contatti" element={<ContattiPage />} />
            <Route path="/carroattrezzi-messina" element={<CarroattrezziMessinaPage />} />
            <Route path="/carroattrezzi-tremestieri" element={<CarroattrezziTremestieriPage />} />
            <Route path="/carroattrezzi-villafranca-tirrena" element={<CarroattrezziVillafrancaPage />} />
            <Route path="/soccorso-stradale-messina" element={<SoccorsoStradaleMessinaPage />} />
            <Route path="/soccorso-stradale-provincia-di-messina" element={<SoccorsoProvinciaMessinaPage />} />
            <Route path="/soccorso-stradale-milazzo" element={<SoccorsoStradaleMilazzoPage />} />
            <Route path="/soccorso-stradale-taormina" element={<SoccorsoStradaleTaorminaPage />} />
            <Route path="/soccorso-autostradale-a18-a20" element={<SoccorsoAutostradalePage />} />
            <Route path="/soccorso-mezzi-pesanti-messina" element={<SoccorsoMezziPesantiPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/chi-siamo" element={<Navigate to="/contatti" replace />} />
            <Route path="/servizi" element={<Navigate to="/" replace />} />
            <Route path="/index.php/sample-page" element={<Navigate to="/" replace />} />
            <Route path="/area-riservata/login" element={<PrivateLoginPage />} />
            <Route path="/area-riservata" element={<ProtectedRoute><PrivateDashboardPage /></ProtectedRoute>} />
            <Route
              path="/gestione-foto"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <PhotoManagementPage
                    photoConfig={photoConfig}
                    onConfigSaved={(data) => setPhotoConfig({
                      slots: { ...DEFAULT_IMAGE_SLOTS, ...data.slots },
                      galleryPhotos: data.gallery_photos,
                      library: data.library,
                      updated_at: data.updated_at,
                    })}
                    isLoadingConfig={isLoadingConfig}
                  />
                </ProtectedRoute>
              }
            />
          </Routes>
          <CookieConsentBanner />
          <TextEditorControls />
          <Toaster />
        </BrowserRouter>
      </TextEditorProvider>
    </AuthProvider>
  );
}

export default App;
